/**
 * A PDF plan set on an infinite canvas.
 *
 * The thumbnail rail is an index into one flat field of sheets: clicking flies
 * the camera, and zooming out is how you get the whole set. Sheets are locked,
 * so the canvas over them is a markup layer. See `pdf-plan-set.ts` for the
 * level-of-detail scheme that makes the flat field affordable.
 */
import { ChevronDown, ChevronUp, PanelLeft, Search, X } from "lucide-react";
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createShapeId,
  createTLStore,
  defaultShapeUtils,
  react,
  Tldraw,
  useEditor,
  useValue,
  type BoxModel,
  type Editor,
  type TLAssetId,
  type TLCameraOptions,
} from "tldraw";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  openPlanSet,
  type PlanSet,
  type PlanSheet,
  type SheetMatch,
  type SheetTile,
} from "@/panes/pdf-plan-set";
import { usePublishPaneActions, useWorkspacePane } from "@/panes/Workspace";

import "tldraw/tldraw.css";

interface PdfPaneProps {
  paneId: string;
  filename: string;
  src?: string;
}

const PdfPane = ({ paneId, filename, src }: PdfPaneProps) => {
  if (!src) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
        {`PDF "${filename}" not found.`}
      </div>
    );
  }

  return <PlanSetCanvas paneId={paneId} src={src} />;
};

export default PdfPane;

const PlanSetCanvas = ({ paneId, src }: { paneId: string; src: string }) => {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [planSet, setPlanSet] = useState<PlanSet | null>(null);
  const [thumbnails, setThumbnails] = useState<readonly (string | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showIndex, setShowIndex] = useState(true);
  const paneRef = useRef<HTMLDivElement>(null);
  const { focusPane } = useWorkspacePane(paneId);

  // Read by the asset store below, which outlives any one render.
  const planSetRef = useRef<PlanSet | null>(null);
  planSetRef.current = planSet;

  // Every sheet shape is its overview render, whatever the camera is doing.
  // Detail is not this layer's job: `DetailTiles` redraws what the viewport
  // covers on top, which is what lets the zoom go past a fixed pixel budget.
  const store = useMemo(
    () =>
      createTLStore({
        shapeUtils: defaultShapeUtils,
        assets: {
          upload: async () => ({ src: "" }),
          resolve: (asset) => {
            const set = planSetRef.current;
            const index = asset.meta.sheetIndex;
            if (!set || typeof index !== "number") return null;
            return set.renderOverview(index).then((render) => render.url);
          },
        },
      }),
    [],
  );

  useEffect(() => {
    let disposed = false;
    let opened: PlanSet | null = null;

    openPlanSet(src).then(
      (set) => {
        if (disposed) return set.dispose();
        opened = set;
        setPlanSet(set);
        setThumbnails(set.sheets.map(() => null));
      },
      (cause: Error) => {
        if (!disposed) setError(cause.message);
      },
    );

    return () => {
      disposed = true;
      opened?.dispose();
    };
  }, [src]);

  // Place each sheet as its render lands, so a long set fills in progressively.
  useEffect(() => {
    if (!editor || !planSet) return;
    let cancelled = false;

    // The field's geometry is known before any pixels are, so frame it now:
    // sheets then pop into a camera that is already where it belongs.
    editor.zoomToBounds(planSet.bounds, { inset: 24 });

    (async () => {
      for (const [index, sheet] of planSet.sheets.entries()) {
        const render = await planSet.renderOverview(index);
        if (cancelled) return;

        const asset = `asset:sheet_${index}` as TLAssetId;
        editor.run(
          () => {
            editor.createAssets([
              {
                id: asset,
                typeName: "asset",
                type: "image",
                props: {
                  name: sheet.label,
                  // Resolved per screen scale by the store's asset resolver.
                  src: asset,
                  w: sheet.w,
                  h: sheet.h,
                  mimeType: "image/png",
                  isAnimated: false,
                },
                meta: { sheetIndex: index },
              },
            ]);
            editor.createShapes([
              {
                id: createShapeId(),
                type: "image",
                x: sheet.x,
                y: sheet.y,
                isLocked: true,
                props: { assetId: asset, w: sheet.w, h: sheet.h },
                meta: { planSheet: sheet.pageNumber },
              },
            ]);
          },
          { history: "ignore" },
        );

        setThumbnails((prev) =>
          prev.map((u, i) => (i === index ? render.url : u)),
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editor, planSet]);

  const focusedIndex = useValue(
    "focused sheet",
    () => (editor && planSet ? sheetAt(planSet.sheets, editor) : -1),
    [editor, planSet],
  );

  const cameraOptions = useMemo<Partial<TLCameraOptions>>(
    () => ({
      wheelBehavior: "zoom",
      // In points: ~0.05 is the whole set, ~0.45 is one sheet filling the pane.
      // The top of the range is what `DetailTiles` bought: it used to stop at
      // 4, not because anyone stops reading there but because past it the
      // whole-sheet render was being enlarged rather than redrawn.
      zoomSteps: [0.02, 0.05, 0.1, 0.25, 0.5, 1, 2, 4, 8, 16],
      constraints: planSet
        ? {
            bounds: planSet.bounds,
            padding: { x: 32, y: 32 },
            origin: { x: 0.5, y: 0.5 },
            // fit-max: fit-min leaves the far column off-screen and unreachable.
            initialZoom: "fit-max",
            baseZoom: "default",
            // outside, not contain: contain becomes "keep the whole field in
            // view", which can't hold once you zoom into one sheet, so it pins
            // the camera. outside just keeps the set touching the viewport.
            behavior: "outside",
          }
        : undefined,
    }),
    [planSet],
  );

  const goToSheet = useCallback(
    (sheet: PlanSheet) => {
      editor?.zoomToBounds(sheet, {
        inset: 24,
        animation: { duration: 220 },
      });
    },
    [editor],
  );

  // Wheel and keyboard are gated on the editor holding DOM focus, so hand it
  // over on the way in and let tldraw drop it when focus moves on. Blurring it
  // ourselves is what silently cancelled every drag.
  const takeFocus = useCallback(() => {
    focusPane();
    editor?.focus();
  }, [editor, focusPane]);

  const search = useSheetSearch(planSet, editor);
  const openSearch = search.open;

  // ⌘F belongs to the pane the pointer is in, not the browser.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "f" && (event.metaKey || event.ctrlKey)) {
        if (!paneRef.current?.contains(document.activeElement)) return;
        event.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openSearch]);

  const hits = useMemo(
    () => ({ matches: search.matches, activeIndex: search.activeIndex }),
    [search.matches, search.activeIndex],
  );

  usePublishPaneActions(
    paneId,
    useMemo(
      () => (
        <PlanControls
          showIndex={showIndex}
          onToggleIndex={() => setShowIndex((shown) => !shown)}
          search={search}
        />
      ),
      [showIndex, search],
    ),
  );

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-destructive">
        {`Could not read this PDF: ${error}`}
      </div>
    );
  }

  return (
    <div
      ref={paneRef}
      className="flex h-full w-full outline-none"
      data-workspace-pane
      onPointerDownCapture={takeFocus}
    >
      <div className="flex min-h-0 flex-1">
        {showIndex ? (
          <SheetRail
            sheets={planSet?.sheets ?? []}
            thumbnails={thumbnails}
            focusedIndex={focusedIndex}
            onSelect={goToSheet}
          />
        ) : null}
        <div className="relative min-w-0 flex-1">
          <PlanSetContext.Provider value={planSet}>
            <SearchHits.Provider value={hits}>
              <Tldraw
                licenseKey={import.meta.env.VITE_TLDRAW_LICENSE_KEY}
                store={store}
                cameraOptions={cameraOptions}
                // Nothing here is editable yet, so the whole editing UI goes: no
                // toolbar, style panel, pages or menus. Drop when markup lands.
                hideUi
                options={{ maxPages: 0, edgeScrollSpeed: 0 }}
                components={CANVAS_COMPONENTS}
                onMount={(instance) => {
                  setEditor(instance);
                  instance.setCurrentTool("hand");
                  // Runs after tldraw registers its own handlers, so these win.
                  for (const type of EXTERNAL_CONTENT) {
                    instance.registerExternalContentHandler(type, () => {});
                  }
                }}
              />
            </SearchHits.Provider>
          </PlanSetContext.Provider>
        </div>
      </div>
    </div>
  );
};

/** Published into the pane's own title bar, so the viewer has no second bar. */
const PlanControls = ({
  showIndex,
  onToggleIndex,
  search,
}: {
  showIndex: boolean;
  onToggleIndex: () => void;
  search: SheetSearch;
}) => (
  <>
    {search.isOpen ? (
      <>
        <SearchStatus search={search} />
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={search.inputRef}
            value={search.query}
            onChange={(event) => search.setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                search.step(event.shiftKey ? -1 : 1);
              } else if (event.key === "Escape") {
                search.close();
              }
            }}
            placeholder="Search sheet text"
            aria-label="Search sheet text"
            className="h-7 w-48 pl-7 text-xs"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          title="Previous match"
          disabled={search.matches.length === 0}
          onClick={() => search.step(-1)}
        >
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          title="Next match"
          disabled={search.matches.length === 0}
          onClick={() => search.step(1)}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          title="Close search"
          onClick={search.close}
        >
          <X className="h-4 w-4" />
        </Button>
      </>
    ) : (
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={search.open}
        title="Search sheet text (⌘F)"
      >
        <Search className="h-4 w-4" />
      </Button>
    )}
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={onToggleIndex}
      aria-pressed={showIndex}
      title={showIndex ? "Hide sheet index" : "Show sheet index"}
    >
      <PanelLeft className="h-4 w-4" />
    </Button>
  </>
);

/**
 * Match count, and the one caveat that matters: a scanned sheet has no text
 * layer, so silence about it would read as "not on the plans".
 */
const SearchStatus = ({ search }: { search: SheetSearch }) => {
  if (search.busy) {
    return <span className="text-xs text-muted-foreground">Searching...</span>;
  }
  if (!search.query.trim()) return null;

  const skipped = search.blindSheets.length;
  return (
    <span className="whitespace-nowrap text-xs text-muted-foreground">
      {search.matches.length === 0
        ? "No matches"
        : `${search.activeIndex + 1} of ${search.matches.length}`}
      {skipped > 0 ? ` (${skipped} scanned sheets not searchable)` : ""}
    </span>
  );
};

/** How much sheet to show around a hit, in points: about 12in x 8in. */
const READING_WINDOW = { w: 900, h: 620 };

function useSheetSearch(planSet: PlanSet | null, editor: Editor | null) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<readonly SheetMatch[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [blindSheets, setBlindSheets] = useState<readonly number[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const goTo = useCallback(
    (match: SheetMatch) => {
      editor?.zoomToBounds(
        {
          x: match.x + match.w / 2 - READING_WINDOW.w / 2,
          y: match.y + match.h / 2 - READING_WINDOW.h / 2,
          ...READING_WINDOW,
        },
        { animation: { duration: 220 } },
      );
    },
    [editor],
  );

  useEffect(() => {
    if (!planSet || !query.trim()) {
      setMatches([]);
      // Also clears a run cancelled mid-flight, which otherwise leaves the
      // toolbar reading "Searching..." over an empty box forever.
      setBusy(false);
      return;
    }
    setBusy(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      const found = await planSet.search(query);
      const blind = await planSet.unsearchableSheets();
      if (cancelled) return;
      setMatches(found);
      setBlindSheets(blind);
      setActiveIndex(0);
      setBusy(false);
      if (found[0]) goTo(found[0]);
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [planSet, query, goTo]);

  const step = useCallback(
    (delta: number) => {
      if (matches.length === 0) return;
      const next = (activeIndex + delta + matches.length) % matches.length;
      setActiveIndex(next);
      goTo(matches[next]);
    },
    [activeIndex, matches, goTo],
  );

  const open = useCallback(() => {
    setIsOpen(true);
    // The input mounts in the same commit, so focus on the next frame.
    requestAnimationFrame(() => inputRef.current?.select());
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setMatches([]);
  }, []);

  return {
    isOpen,
    query,
    setQuery,
    matches,
    activeIndex,
    busy,
    blindSheets,
    inputRef,
    open,
    close,
    step,
  };
}

type SheetSearch = ReturnType<typeof useSheetSearch>;

/**
 * Hits drawn in page space.
 *
 * `OnTheCanvas` renders inside tldraw's camera-transformed layer, so page
 * coordinates land on the sheet and the browser does the panning for free: no
 * React render per camera frame. It is a sibling of the shape layer, and tldraw
 * gives shapes z-indices in the thousands, hence the very large one here.
 */
const SearchHits = createContext<{
  matches: readonly SheetMatch[];
  activeIndex: number;
}>({ matches: [], activeIndex: 0 });

function SearchHighlights() {
  const { matches, activeIndex } = useContext(SearchHits);
  return (
    <div style={{ position: "absolute", zIndex: 1_000_000 }}>
      {matches.map((match, i) => (
        <div
          key={`${match.sheetIndex}:${match.x}:${match.y}:${i}`}
          style={{
            position: "absolute",
            left: match.x,
            top: match.y,
            width: match.w,
            height: match.h,
            backgroundColor:
              i === activeIndex
                ? "rgba(255,145,0,0.55)"
                : "rgba(255,220,0,0.4)",
            outline:
              i === activeIndex ? "2px solid rgba(255,110,0,0.9)" : "none",
            pointerEvents: "none",
          }}
        />
      ))}
    </div>
  );
}

/** Wait for the camera to stop before spending a redraw on where it landed. */
const SETTLE_MS = 180;

/** How much sheet to draw beyond the viewport, so a nudge stays covered. */
const OVERDRAW = 1.15;

const PlanSetContext = createContext<PlanSet | null>(null);

/**
 * The viewport, redrawn at the resolution it is being viewed at.
 *
 * Sits over the sheet shapes and under the search hits, in the same
 * camera-transformed layer, so the tiles pan with the sheets for free and only
 * have to be replaced when the camera settles somewhere new. Until then the
 * overview render shows through, which is the same picture at lower resolution
 * rather than a hole.
 *
 * The canvases are put in by hand rather than through React: they are pixels
 * that arrive from an async render, and going through state would mean a commit
 * that paints nothing followed by a ref effect that paints.
 */
function DetailTiles() {
  const editor = useEditor();
  const planSet = useContext(PlanSetContext);
  const layer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!planSet) return;
    let timer: ReturnType<typeof setTimeout>;
    let controller = new AbortController();

    // A reactor rather than `useValue`: the camera changes every frame and
    // hearing about it must not cost a React render.
    const stop = react("plan detail tiles", () => {
      const region = expand(editor.getViewportPageBounds(), OVERDRAW);
      const scale =
        editor.getZoomLevel() * editor.getInstanceState().devicePixelRatio;

      clearTimeout(timer);
      controller.abort();
      controller = new AbortController();
      const { signal } = controller;

      timer = setTimeout(async () => {
        const tiles = await planSet
          .renderTiles(region, scale, signal)
          .catch((cause: Error) => {
            // The only expected failure is this render being overtaken.
            if (!signal.aborted) throw cause;
            return null;
          });
        if (!tiles || signal.aborted || !layer.current) return;
        for (const stale of layer.current.children) {
          // Let go of the pixels now rather than at the GC's convenience.
          (stale as HTMLCanvasElement).width = 0;
        }
        layer.current.replaceChildren(...tiles.map(place));
      }, SETTLE_MS);
    });

    return () => {
      stop();
      clearTimeout(timer);
      controller.abort();
    };
  }, [editor, planSet]);

  return <div ref={layer} style={{ position: "absolute", zIndex: 500_000 }} />;
}

/** A tile's canvas sized in points, so the camera scales it like everything else. */
function place(tile: SheetTile): HTMLCanvasElement {
  const { canvas } = tile;
  canvas.style.position = "absolute";
  canvas.style.left = `${tile.x}px`;
  canvas.style.top = `${tile.y}px`;
  canvas.style.width = `${tile.w}px`;
  canvas.style.height = `${tile.h}px`;
  canvas.style.pointerEvents = "none";
  return canvas;
}

function expand(box: BoxModel, factor: number): BoxModel {
  const w = box.w * factor;
  const h = box.h * factor;
  return { x: box.x - (w - box.w) / 2, y: box.y - (h - box.h) / 2, w, h };
}

function CanvasLayers() {
  return (
    <>
      <DetailTiles />
      <SearchHighlights />
    </>
  );
}

const CANVAS_COMPONENTS = { OnTheCanvas: CanvasLayers };

/**
 * Every kind of content a drop or paste can carry, all of it refused: this is
 * a viewer, not a canvas you can add to. The asset store only knows how to
 * resolve plan sheets, so anything else that landed would be a blank shape.
 */
const EXTERNAL_CONTENT = [
  "embed",
  "excalidraw",
  "file-replace",
  "files",
  "svg-text",
  "text",
  "tldraw",
  "url",
] as const;

const SheetRail = memo(function SheetRail({
  sheets,
  thumbnails,
  focusedIndex,
  onSelect,
}: {
  sheets: readonly PlanSheet[];
  thumbnails: readonly (string | null)[];
  focusedIndex: number;
  onSelect: (sheet: PlanSheet) => void;
}) {
  const activeRef = useRef<HTMLLIElement>(null);

  // Keep the rail pointed at whatever the camera is over. "nearest" means it
  // only scrolls when the sheet has actually left the strip.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusedIndex]);

  return (
    <aside className="w-40 shrink-0 overflow-y-auto border-r border-border bg-muted/30 p-2">
      {sheets.length === 0 ? (
        <p className="p-2 text-xs text-muted-foreground">Reading plan set...</p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {sheets.map((sheet, index) => (
          <li
            key={sheet.pageNumber}
            ref={index === focusedIndex ? activeRef : undefined}
          >
            <button
              type="button"
              onClick={() => onSelect(sheet)}
              className={`w-full rounded-sm border p-1 text-left transition-colors ${
                index === focusedIndex
                  ? "border-primary bg-background"
                  : "border-transparent hover:border-border"
              }`}
            >
              <span
                className="block bg-background"
                style={{ aspectRatio: `${sheet.w} / ${sheet.h}` }}
              >
                {thumbnails[index] ? (
                  <img
                    src={thumbnails[index]!}
                    alt={sheet.label}
                    // The rail is an index, not a source of shapes. A native
                    // image drag hands the canvas a data: URL it can only read
                    // as a bookmark, and bookmarks must be real http(s) URLs.
                    draggable={false}
                    className="h-full w-full object-contain"
                  />
                ) : null}
              </span>
              <span className="mt-1 block truncate text-xs text-muted-foreground">
                {sheet.label}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
});

/** The sheet under the viewport centre, or the nearest when it's in a gutter. */
function sheetAt(sheets: readonly PlanSheet[], editor: Editor): number {
  const { x, y } = editor.getViewportPageBounds().center;

  let nearest = -1;
  let nearestDistance = Infinity;
  for (const [index, sheet] of sheets.entries()) {
    const dx = Math.max(sheet.x - x, 0, x - (sheet.x + sheet.w));
    const dy = Math.max(sheet.y - y, 0, y - (sheet.y + sheet.h));
    const distance = dx * dx + dy * dy;
    if (distance === 0) return index;
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = index;
    }
  }
  return nearest;
}
